import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentAPI } from '../../services/api';

function AddonPaymentVerification() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, failed
  const [message, setMessage] = useState('Verifying your payment...');
  const [attempts, setAttempts] = useState(0);
  const attemptsRef = useRef(0);
  const maxAttempts = 10;

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    try {
      // Increment attempt counter
      attemptsRef.current += 1;
      setAttempts(attemptsRef.current);

      console.log(`Addon payment verification attempt ${attemptsRef.current} of ${maxAttempts}`);

      // Get the stored order ID and request ID
      const cashfreeOrderId = localStorage.getItem('pendingAddonPaymentOrderId');
      const requestId = localStorage.getItem('pendingAddonRequestId');

      if (!cashfreeOrderId || !requestId) {
        setStatus('failed');
        setMessage('Payment session not found. Please try again or contact support.');
        return;
      }

      // Verify payment with backend
      const verifyResponse = await paymentAPI.verifyAddonPayment(requestId, cashfreeOrderId);

      if (verifyResponse.data.success) {
        // Backend returns status directly: 'pending_approval', 'payment_failed', etc.
        const paymentStatus = verifyResponse.data.status;

        if (paymentStatus === 'pending_approval' || paymentStatus === 'payment_completed') {
          // Clear stored data
          localStorage.removeItem('pendingAddonPaymentOrderId');
          localStorage.removeItem('pendingAddonRequestId');

          setStatus('success');
          setMessage(verifyResponse.data.message || 'Payment successful! Your add-on request has been submitted for approval. You will be notified once it is approved.');

          setTimeout(() => {
            navigate('/restaurant/addon-subscriptions');
          }, 3000);
        } else if (paymentStatus === 'payment_failed') {
          localStorage.removeItem('pendingAddonPaymentOrderId');
          localStorage.removeItem('pendingAddonRequestId');

          setStatus('failed');
          setMessage('Payment failed. ' + (verifyResponse.data.message || 'Please try again or contact support.'));
        } else {
          // Payment still pending, retry
          if (attemptsRef.current < maxAttempts) {
            setMessage(`Verifying payment... Attempt ${attemptsRef.current} of ${maxAttempts}`);
            setTimeout(() => {
              verifyPayment();
            }, 2000);
          } else {
            setStatus('failed');
            setMessage('Payment verification is taking longer than expected. Please contact support if the amount was deducted.');
          }
        }
      } else {
        // Check if it's a definite failure or just pending
        const errorStatus = verifyResponse.data.status;

        if (errorStatus === 'payment_failed') {
          localStorage.removeItem('pendingAddonPaymentOrderId');
          localStorage.removeItem('pendingAddonRequestId');
          setStatus('failed');
          setMessage(verifyResponse.data.message || 'Payment failed. Please try again or contact support.');
        } else if (attemptsRef.current < maxAttempts) {
          // Verification call failed or pending, retry
          setMessage(`Verifying payment... Attempt ${attemptsRef.current} of ${maxAttempts}`);
          setTimeout(() => {
            verifyPayment();
          }, 2000);
        } else {
          setStatus('failed');
          setMessage(verifyResponse.data.message || 'Payment verification failed. Please contact support if the amount was deducted.');
        }
      }
    } catch (error) {
      console.error('Payment verification error:', error);

      // Retry if under max attempts
      if (attemptsRef.current < maxAttempts) {
        setMessage(`Verifying payment... Attempt ${attemptsRef.current} of ${maxAttempts}`);
        setTimeout(() => {
          verifyPayment();
        }, 2000);
      } else {
        setStatus('failed');
        setMessage('Payment verification failed. Please contact support if the amount was deducted.');
      }
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#F8FAFC',
      padding: '24px'
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        padding: '48px 32px',
        maxWidth: '480px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 10px 15px rgba(0, 0, 0, 0.05)',
        animation: 'fadeIn 0.3s ease-in-out'
      }}>
        {/* Status Icon */}
        <div style={{
          width: '96px',
          height: '96px',
          backgroundColor: status === 'success'
            ? '#10B981'
            : status === 'failed'
            ? '#EF4444'
            : '#FF6961',
          borderRadius: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 32px',
          animation: status === 'verifying' ? 'pulse 1.5s ease-in-out infinite' : 'scaleIn 0.4s ease-out'
        }}>
          {status === 'verifying' && (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" style={{
              animation: 'spin 2s linear infinite'
            }}>
              <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeLinecap="round"
                strokeDasharray="31.4 31.4" strokeDashoffset="10" opacity="0.3"/>
              <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeLinecap="round"
                strokeDasharray="31.4 31.4"/>
            </svg>
          )}
          {status === 'success' && (
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          {status === 'failed' && (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="3" strokeLinecap="round"/>
            </svg>
          )}
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#0F172A',
          marginBottom: '16px',
          letterSpacing: '-0.5px'
        }}>
          {status === 'verifying' && 'Verifying Payment'}
          {status === 'success' && 'Payment Successful!'}
          {status === 'failed' && 'Verification Issue'}
        </h1>

        {/* Message */}
        <p style={{
          fontSize: '16px',
          color: '#64748B',
          marginBottom: '32px',
          lineHeight: '1.6'
        }}>
          {message}
        </p>

        {/* Progress Indicator for Verifying State */}
        {status === 'verifying' && (
          <div style={{
            marginBottom: '32px'
          }}>
            <div style={{
              width: '100%',
              height: '6px',
              backgroundColor: '#E2E8F0',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${(attempts / maxAttempts) * 100}%`,
                height: '100%',
                backgroundColor: '#FF6961',
                borderRadius: '8px',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
            <p style={{
              fontSize: '14px',
              color: '#94A3B8',
              marginTop: '12px'
            }}>
              Attempt {attempts} of {maxAttempts}
            </p>
          </div>
        )}

        {/* Success Info */}
        {status === 'success' && (
          <div style={{
            backgroundColor: '#F0FDF4',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            border: '1px solid #BBF7D0'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '20px' }}>🎉</span>
              <span style={{
                fontSize: '14px',
                color: '#166534',
                fontWeight: '500'
              }}>
                Redirecting to Add-ons page...
              </span>
            </div>
          </div>
        )}

        {/* Action Buttons for Failed State */}
        {status === 'failed' && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '100%'
          }}>
            <button
              onClick={() => navigate('/restaurant/addon-subscriptions')}
              style={{
                backgroundColor: '#FF6961',
                color: '#FFFFFF',
                fontSize: '16px',
                fontWeight: '600',
                padding: '14px 24px',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(255, 105, 97, 0.2)'
              }}
            >
              Back to Add-ons
            </button>
            <button
              onClick={() => window.location.href = 'mailto:support@foodwinc.com'}
              style={{
                backgroundColor: '#FFFFFF',
                color: '#64748B',
                fontSize: '16px',
                fontWeight: '600',
                padding: '14px 24px',
                borderRadius: '12px',
                border: '2px solid #E2E8F0',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Contact Support
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            transform: scale(0.8);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.9;
          }
        }
      `}</style>
    </div>
  );
}

export default AddonPaymentVerification;
